import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';
import {
  CHAT_V2_RECENT_SESSION_LIMIT,
  CHAT_V2_SIDEBAR_COLLAPSED_WIDTH,
  CHAT_V2_SIDEBAR_EXPANDED_WIDTH,
} from './chatV2Constants';
import { sessionTitle } from './chatSessionListUtils';
import {
  CHAT_SIDEBAR_COLLAPSE_BUTTON_SX,
  CHAT_SIDEBAR_DIVIDER_SX,
  CHAT_SIDEBAR_GUTTER_COLLAPSED_PX,
  CHAT_SIDEBAR_GUTTER_PX,
  CHAT_SIDEBAR_HEADER_ROW_SX,
  CHAT_SIDEBAR_HEADER_TITLE_SX,
  CHAT_SIDEBAR_NAV_SECTION_SX,
  CHAT_SIDEBAR_SECTION_LABEL_SX,
  chatSidebarLibraryActionSx,
  chatSidebarNavItemSx,
  chatSidebarRecentItemSx,
  chatSidebarShellSx,
  getChatSidebarNavActive,
} from './chatSidebarStyles';
import {
  CHAT_MOBILE_HISTORY_BAR_SX,
  CHAT_SIDEBAR_DRAWER_PAPER_SX,
} from './chatStyles';

function numberOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function formattedNumber(value) {
  return value.toLocaleString();
}

function resetLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Resets ${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

function ChatCreditReadout({ summary, loading, error }) {
  if (loading) {
    return (
      <>
        <Divider sx={{ ...CHAT_SIDEBAR_DIVIDER_SX, mt: 0, mb: 1.25 }} />
        <Box aria-label="Loading credits" sx={{ px: `${CHAT_SIDEBAR_GUTTER_PX}px`, pb: 1.5 }}>
          <Skeleton variant="text" width="72%" />
          <Skeleton variant="text" width="48%" />
        </Box>
      </>
    );
  }

  const total = numberOrNull(summary?.total) ?? numberOrNull(summary?.balance);
  const monthly = numberOrNull(summary?.monthly_credits);
  const standalone = numberOrNull(summary?.standalone_credits) ?? 0;
  const allowance = numberOrNull(summary?.monthly_credit_allowance);
  const unavailable = Boolean(error) || total === null;
  const hasAllowance = allowance !== null && allowance > 0 && monthly !== null;
  const progress = hasAllowance
    ? Math.max(0, Math.min(100, (monthly / allowance) * 100))
    : null;
  const reset = resetLabel(summary?.monthly_credit_reset_at);

  return (
    <>
      <Divider sx={{ ...CHAT_SIDEBAR_DIVIDER_SX, mt: 0, mb: 1.25 }} />
      <Box sx={{ px: `${CHAT_SIDEBAR_GUTTER_PX}px`, pb: 1.5 }}>
        {!unavailable ? (
          <Typography component="p" sx={{ ...CHAT_SIDEBAR_SECTION_LABEL_SX, px: 0, pb: 0.25 }}>
            {hasAllowance ? 'Monthly credits remaining' : 'Credits'}
          </Typography>
        ) : null}
        {unavailable ? (
          <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: '18px', color: 'text.primary' }}>
            Credits unavailable
          </Typography>
        ) : hasAllowance ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: '18px', color: 'text.primary' }}>
              {formattedNumber(monthly)} / {formattedNumber(allowance)}
            </Typography>
            <LinearProgress
              aria-label="Monthly credits remaining"
              variant="determinate"
              value={progress}
              sx={{
                my: 1,
                height: 6,
                borderRadius: 999,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': { borderRadius: 999 },
              }}
            />
            <Typography component="p" sx={{ ...CHAT_SIDEBAR_SECTION_LABEL_SX, px: 0, pb: 0 }}>
              {formattedNumber(total)} total available · {formattedNumber(standalone)} standalone
            </Typography>
            {reset ? (
              <Typography component="p" sx={{ ...CHAT_SIDEBAR_SECTION_LABEL_SX, px: 0, pb: 0 }}>
                {reset}
              </Typography>
            ) : null}
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: '18px', color: 'text.primary' }}>
              {formattedNumber(total)} total available
            </Typography>
            {standalone > 0 ? (
              <Typography component="p" sx={{ ...CHAT_SIDEBAR_SECTION_LABEL_SX, px: 0, pb: 0 }}>
                {formattedNumber(standalone)} standalone
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    </>
  );
}

export function ChatMobileHistoryBarV2({ onOpen, title = 'Chat' }) {
  return (
    <Box sx={CHAT_MOBILE_HISTORY_BAR_SX} data-testid="chat-mobile-top-bar-v2">
      <Typography sx={CHAT_SIDEBAR_HEADER_TITLE_SX}>
        {title}
      </Typography>
      <Tooltip title="Open sidebar">
        <IconButton aria-label="Open sidebar" onClick={onOpen} size="small" sx={CHAT_SIDEBAR_COLLAPSE_BUTTON_SX}>
          <MenuOpenOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function ChatSidebarDrawerV2({ open, onClose, sidebarProps }) {
  return (
    <Drawer
      data-testid="chat-sidebar-drawer-v2"
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      PaperProps={{ sx: { ...CHAT_SIDEBAR_DRAWER_PAPER_SX, width: CHAT_V2_SIDEBAR_EXPANDED_WIDTH } }}
    >
      <ChatSidebarV2 {...sidebarProps} variant="temporary" collapsed={false} onClose={onClose} />
    </Drawer>
  );
}

function ChatLibraryNav({
  active = false,
  collapsed = false,
  disabled = false,
  onClick,
}) {
  const theme = useTheme();
  const label = 'Chat Library';

  if (collapsed) {
    const iconButton = (
      <IconButton
        data-testid="chat-nav-library"
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        disabled={disabled}
        onClick={onClick}
        size="small"
        sx={chatSidebarLibraryActionSx(theme, { active, collapsed: true })}
      >
        <LibraryBooksOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    );

    return (
      <Tooltip title={label} placement="right">
        <span>{iconButton}</span>
      </Tooltip>
    );
  }

  return (
    <Button
      data-testid="chat-nav-library"
      fullWidth
      variant="text"
      size="small"
      disabled={disabled}
      onClick={onClick}
      startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 16 }} />}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      sx={chatSidebarLibraryActionSx(theme, { active, collapsed: false })}
    >
      {label}
    </Button>
  );
}

export default function ChatSidebarV2({
  sessions = [],
  loading = false,
  error = null,
  restoreError = null,
  creditSummary = null,
  creditLoading = false,
  creditError = null,
  selectedSessionId = null,
  disabled = false,
  activeView = 'chat',
  collapsed = false,
  showRecentChats = false,
  onNewChat,
  onOpenLibrary,
  onSelectSession,
  onRetrySessions,
  onToggleCollapsed,
  onClose,
  variant = 'persistent',
}) {
  const theme = useTheme();
  const isTemporary = variant === 'temporary';
  const isCollapsedDesktop = collapsed && !isTemporary;
  const width = isCollapsedDesktop
    ? CHAT_V2_SIDEBAR_COLLAPSED_WIDTH
    : CHAT_V2_SIDEBAR_EXPANDED_WIDTH;
  const recentSessions = sessions.slice(0, CHAT_V2_RECENT_SESSION_LIMIT);
  const showRecentChatsSection = showRecentChats && (!collapsed || isTemporary);
  const showCreditReadout = !isCollapsedDesktop;
  const navActive = getChatSidebarNavActive({ activeView, selectedSessionId });
  const newChatActive = navActive === 'new';
  const libraryActive = navActive === 'library';

  return (
    <Box
      data-testid="chat-sidebar-v2"
      data-sidebar-variant={variant}
      data-sidebar-collapsed={String(isCollapsedDesktop)}
      sx={[
        {
          width,
          flex: isTemporary ? undefined : `0 0 ${width}px`,
        },
        chatSidebarShellSx({ isTemporary }),
      ]}
    >
      <Box
        sx={{
          ...CHAT_SIDEBAR_HEADER_ROW_SX,
          px: isCollapsedDesktop ? `${CHAT_SIDEBAR_GUTTER_COLLAPSED_PX}px` : `${CHAT_SIDEBAR_GUTTER_PX}px`,
          justifyContent: isCollapsedDesktop ? 'center' : 'space-between',
        }}
      >
        {!isCollapsedDesktop ? (
          <Typography component="h2" sx={CHAT_SIDEBAR_HEADER_TITLE_SX}>
            Chat
          </Typography>
        ) : null}
        {isTemporary ? (
          <Tooltip title="Close sidebar">
            <IconButton aria-label="Close sidebar" onClick={onClose} size="small" sx={CHAT_SIDEBAR_COLLAPSE_BUTTON_SX}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <IconButton
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={onToggleCollapsed}
              size="small"
              sx={{
                ...CHAT_SIDEBAR_COLLAPSE_BUTTON_SX,
                ml: isCollapsedDesktop ? 0 : 'auto',
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          ...CHAT_SIDEBAR_NAV_SECTION_SX,
          px: isCollapsedDesktop ? `${CHAT_SIDEBAR_GUTTER_COLLAPSED_PX}px` : `${CHAT_SIDEBAR_GUTTER_PX}px`,
        }}
      >
        {isCollapsedDesktop ? (
          <Tooltip title="New Chat" placement="right">
            <span>
              <IconButton
                aria-label="New Chat"
                aria-current={newChatActive ? 'page' : undefined}
                onClick={onNewChat}
                disabled={disabled}
                size="small"
                sx={chatSidebarNavItemSx(theme, { active: newChatActive, collapsed: true })}
              >
                <AddCommentOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            variant="text"
            size="small"
            startIcon={<AddCommentOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onNewChat}
            disabled={disabled}
            aria-current={newChatActive ? 'page' : undefined}
            sx={chatSidebarNavItemSx(theme, { active: newChatActive, collapsed: false })}
          >
            New Chat
          </Button>
        )}

        <ChatLibraryNav
          active={libraryActive}
          collapsed={isCollapsedDesktop}
          disabled={disabled}
          onClick={onOpenLibrary}
        />
      </Box>

      {showRecentChatsSection ? (
        <>
          <Divider sx={CHAT_SIDEBAR_DIVIDER_SX} />

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Typography component="p" sx={CHAT_SIDEBAR_SECTION_LABEL_SX}>
              Recent Chats
            </Typography>

            <Box sx={{ px: `${CHAT_SIDEBAR_GUTTER_PX}px`, pb: 1.5 }}>
              {loading ? (
                <Box aria-label="Loading recent chats">
                  {[0, 1, 2].map((item) => (
                    <Skeleton key={item} variant="rounded" height={36} sx={{ mb: 0.5, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : null}
              {!loading && error ? (
                <Alert
                  severity="warning"
                  sx={{ my: 0.375, py: 0.25, fontSize: 12 }}
                  action={(
                    <Button color="inherit" size="small" onClick={onRetrySessions} sx={{ fontSize: 12 }}>
                      Retry
                    </Button>
                  )}
                >
                  Unable to load recent chats.
                </Alert>
              ) : null}
              {!loading && restoreError ? (
                <Alert severity="error" sx={{ my: 0.375, py: 0.25, fontSize: 12 }}>
                  {restoreError}
                </Alert>
              ) : null}
              {!loading && recentSessions.length > 0 ? (
                <List aria-label="Recent chats" disablePadding>
                  {recentSessions.map((session) => {
                    const title = sessionTitle(session);
                    const selected = activeView === 'chat' && session.session_id === selectedSessionId;

                    return (
                      <ListItemButton
                        key={session.session_id}
                        selected={selected}
                        disabled={disabled}
                        onClick={() => onSelectSession?.(session.session_id)}
                        aria-label={`Open chat: ${title}`}
                        sx={chatSidebarRecentItemSx(theme, { selected })}
                      >
                        <ListItemText
                          primary={title}
                          primaryTypographyProps={{
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              ) : null}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }} />
      )}

      {showCreditReadout ? (
        <ChatCreditReadout
          summary={creditSummary}
          loading={creditLoading}
          error={creditError}
        />
      ) : null}
    </Box>
  );
}
